import * as React from 'react';
import {observer} from 'mobx-react';
import {style} from 'typestyle';
import {center, horizontal, horizontallySpaced} from 'csstips';
import {px, rem, url} from 'csx';
import {colorBackground, colorBrand, colorSubtle} from '../theme/color';
import {resetAnchorStyles} from '../theme/reset';
import {PageCheckoutPanelProps} from './page-checkout-panels';
import {Alert} from './alert';
import {PageCheckoutCouponCode} from './page-checkout-coupon-code';
import {RatecardSchema, RatecardStatisticsSchema} from '../types/schema';
import {formatCurrency, formatVAT} from '../lib/currency';
import {getChannelImageURL} from '../lib/image';
import {noop} from '../lib/helpers';

export interface PageCheckoutPanelStep2Props extends PageCheckoutPanelProps {
  i18n?: {
    title: string;
    titleCoupon: string;
    channel: string;
    summary(ratecard: RatecardSchema, statistics: RatecardStatisticsSchema): string;
    net_label: string;
    net_label_included: string;
    net_value(value: string | number, currency: string, vatRate: string): string;
    discount_label(coupon?: string): string;
    discount_value(value: string | number, currency: string): string;
    vat_label(vatNumber?: string): string;
    vat_value(value: string | number, currency: string): string;
    total_label: string;
    total_value(value: string | number, currency: string): string;
  }
}

const DEFAULT_I18N = {
  title: 'Order summary',
  titleCoupon: 'Discount',
  channel: 'YouTube channel',
  summary: (ratecard: RatecardSchema, statistics: RatecardStatisticsSchema) =>
    `Your price is ${formatCurrency(ratecard.value, ratecard.currency)} based on ${Number(statistics.average_views).toLocaleString()} average video views.`,
  net_label: 'Total price',
  net_value: (value: string | number, currency: string, vatRate: string) => formatCurrency(value, currency) + (vatRate === null ? '' : (vatRate!=='0.00'?` (inc. VAT at ${formatVAT(vatRate)})`:'')),
  discount_label: (coupon?: string) => 'Discount price' + (coupon ? ` (${coupon})`:''),
  discount_value: (value: string | number, currency: string) => formatCurrency(value, currency),
  vat_label: (vatNumber?: string) => 'VAT applied' + (vatNumber ? ` (${vatNumber})`:''),
  vat_value: (value: string | number, currency: string) => formatCurrency(value, currency),
  total_label: 'To pay',
  total_value: (value: string | number, currency: string) => formatCurrency(value, currency),
};

export const PageCheckoutPanelStep2 = observer<PageCheckoutPanelStep2Props>(
  ({ model, i18n = DEFAULT_I18N }) => {
    const { total, netTotal, discountAmount, coupon, channel, currency, vatRate } = model;

    return (
      <div className={styles.container}>
        <h5>{i18n.title}</h5>
        <div className={styles.account}>
          <div style={{ backgroundImage: url(getChannelImageURL(model.channel)) }} />
          <div>
            <div className={styles.label}>{i18n.channel}</div>
            <div>
              <a className={styles.link} href={`https://www.youtube.com/channel/${model.channel.id}`} target="_blank">
                {model.channel.name || model.channel.id}
              </a>
            </div>
          </div>
        </div>
        <div className={styles.summary}>
          {i18n.summary(channel.ratecard, channel.ratecard_statistics)}
        </div>
        <dl className={styles.period}>
          <dt>{i18n.net_label}</dt>
          <dd>{i18n.net_value(netTotal, currency, vatRate)}</dd>
          <dt>{i18n.discount_label(coupon)}</dt>
          <dd>{i18n.discount_value(discountAmount, currency)}</dd>
          <dt>{i18n.total_label}</dt>
          <dd>{i18n.total_value(total, currency)}</dd>
        </dl>
        <div className={styles.section}>
          <h5>{i18n.titleCoupon}</h5>
          <PageCheckoutCouponCode
            model={model.couponForm}
            onChange={noop}
          />
          {model.couponText && (
            <Alert title={model.couponText.title} type="warning" position="top-left">
              <p>{model.couponText.message}</p>
            </Alert>
          )}
        </div>
      </div>
    )
  }
);

const circleRadius = rem(4.8);

const styles = {
  container: style({
    $nest: {
      '& h5': {
        margin: '0 0 1rem 0'
      }
    }
  }),
  account: style({
    ...horizontal,
    ...horizontallySpaced(15),
    ...center,
    padding: `0 0 0.75rem 0`,
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
    $nest: {
      '&> div:first-child': {
        flexShrink: 0,
        width: circleRadius,
        height: circleRadius,
        borderRadius: circleRadius,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      '&> div:last-child': {
        overflow: 'hidden'
      },
      '&> div > div': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre'
      }
    }
  }),
  label: style({
    color: colorSubtle.toString(),
    fontSize: rem(0.875),
    lineHeight: rem(1.2)
  }),
  link: style({
    color: colorBrand.toString(),
    fontWeight: 'bold',
    fontSize: rem(1.125),
    lineHeight: rem(1.2),
    $nest: {
      ...resetAnchorStyles(colorBrand.toString())
    }
  }),
  period: style({
    margin: 0,
    padding: '1rem 0',
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
    $nest: {
      '& dt': {
        margin: 0,
        padding: 0,
        color: colorSubtle.toString(),
        fontSize: rem(0.875),
        lineHeight: rem(1.2)
      },
      '& dd': {
        margin: '0 0 1rem 0',
        padding: 0,
        fontSize: rem(1),
        fontWeight: 'bold',
        lineHeight: rem(1.2)
      },
      '& dd:last-child': {
        margin: 0,
        fontSize: rem(1.5),
        lineHeight: rem(1.7)
      }
    }
  }),
  section: style({
    borderTop: `1px solid ${colorBackground.fade(0.7).toString()}`,
    padding: '1rem 0'
  }),
  summary: style({
    padding: '1rem 0',
    fontSize: rem(1),
    textAlign: 'center',
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
  })
};